// ─── App-host (app.wefixtrades.com) detection and cross-host URLs ────────────
//
// WHY THIS EXISTS
// ---------------
// The tool portal is a PUBLIC CONVENIENCE SURFACE that lives on its own host,
// `app.wefixtrades.com`. Marketing keeps its normal menu on `wefixtrades.com`
// and gains a single "See all tools" button pointing here. That split is the
// whole point: one canonical home per page, one navigation per surface.
//
// PR #2074 put the rail on the marketing host, which produced TWO navigations
// on one marketing page (the fixed MarketingNav plus a 250px rail). This module
// is how that gets corrected — the rail is good, it was in the wrong place.
//
// HOST DETECTION IS FIRST-LABEL ONLY, DELIBERATELY
// ------------------------------------------------
// `isAppHostname` asks whether the first DNS label is exactly "app". It does
// not hardcode wefixtrades.com, for three reasons:
//
//   1. It works in local development against `app.localhost:5000`. Chrome (and
//      Firefox) resolve every *.localhost name to loopback, so the real code
//      path — including `window.location.hostname` — is exercised locally
//      without touching the hosts file. A detector that matched only the
//      production domain would be untestable and would therefore be wrong in
//      ways nobody saw until deploy.
//   2. It works on wefixtrades.ca as well as .com, which are both live.
//   3. PRERENDER SAFETY. The prerender step serves `dist/public` from a bare
//      node:http server on 127.0.0.1:4173 and drives it with headless Chromium
//      (scripts/seo/prerender-routes.mjs). There is no Host header rewriting
//      and no Express in that loop, so `window.location.hostname` is
//      "127.0.0.1" for all 126 routes. First label "127" is not "app", so the
//      app-host tree NEVER renders during prerender and cannot fail the build
//      gate. The marketing tree is what gets snapshotted, which is exactly
//      right: the marketing host owns the search index.
//
// This mirrors the existing host-branch precedent in the codebase,
// `hostedSlugFromHost()` in shared/slugUtils.ts, which reads
// window.location.hostname the same way and returns null on 127.0.0.1.
//
// NOTE: "app" is already in RESERVED_SLUGS in shared/slugUtils.ts, so a hosted
// customer calculator can never be served from a host whose first label is
// "app". The two host branches cannot collide.

import { SITE_URL } from "@/lib/seo/pageMeta";

/** The DNS label that selects the tool-portal host. */
export const APP_HOST_LABEL = "app";

/**
 * The marketing origin, and the canonical home of every indexable page.
 * Re-exported from the SEO module rather than redeclared so there is exactly
 * one definition of "where the real site lives" in the client bundle.
 */
export const MARKETING_ORIGIN = SITE_URL;

// NOTE: the app-host chrome bar deliberately has NO height constant of its own.
// It is exactly MarketingNav's card geometry — MARKETING_NAV_H (68) at desktop,
// MARKETING_NAV_H_MOBILE (66) below the breakpoint — because it is a drop-in
// replacement for that fixed card in the same vertical space. Two consequences,
// both of which are the reason for the choice: the rail's reviewed `top` offset
// needs no change at all, and FreeToolLayout's `.ftool-page` / FreeAudit's
// `.audit-page` negative-margin hoist (derived from MARKETING_NAV_H +
// MARKETING_TOP_SPACER) keeps working unmodified on app-host tool pages. A bar
// of any other height would silently shift every one of those pages.

/** True when `hostname`'s first label is exactly "app". */
export function isAppHostname(hostname: string): boolean {
  if (!hostname) return false;
  const first = hostname.toLowerCase().split(".")[0];
  return first === APP_HOST_LABEL;
}

/**
 * True when the CURRENT page is being served from the app host.
 *
 * Returns false when there is no `window` (prerender, any SSR-shaped context),
 * so the marketing tree is the default everywhere that is not demonstrably the
 * app host. Failing closed matters: a false positive here would swap the
 * marketing navigation out on wefixtrades.com, which is the exact defect this
 * change exists to fix.
 */
export function isAppHost(hostname?: string): boolean {
  if (typeof hostname === "string") return isAppHostname(hostname);
  if (typeof window === "undefined") return false;
  return isAppHostname(window.location.hostname);
}

/** Normalize a path to a leading-slash form for URL building. */
function withLeadingSlash(path: string): string {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Absolute URL for `path` on the MARKETING host.
 *
 * Derived from the current location by dropping the leading "app." label, so
 * protocol and port survive. That is what makes `app.localhost:5000` resolve to
 * `http://localhost:5000` in development instead of sending a local click to
 * production. Falls back to the canonical origin when there is no window, or
 * when the current host is not an app host.
 */
export function marketingUrl(path: string): string {
  const p = withLeadingSlash(path);
  if (typeof window === "undefined") return `${MARKETING_ORIGIN}${p}`;
  const { protocol, host, hostname } = window.location;
  if (!isAppHostname(hostname)) return `${MARKETING_ORIGIN}${p}`;
  // Strip exactly one leading "app." label, preserving any :port on `host`.
  const marketingHost = host.replace(/^app\./i, "");
  return `${protocol}//${marketingHost}${p}`;
}

/**
 * Absolute URL for `path` on the APP host — the target of the marketing
 * header's "See all tools" button.
 *
 * Mirror image of `marketingUrl`: adds the "app." label to the current host so
 * a development click goes to `http://app.localhost:5000` rather than to
 * production. Falls back to the canonical app origin without a window.
 */
export function appHostUrl(path: string = "/"): string {
  const p = withLeadingSlash(path);
  if (typeof window === "undefined") {
    return `${MARKETING_ORIGIN.replace(/^https:\/\//i, `https://${APP_HOST_LABEL}.`)}${p}`;
  }
  const { protocol, host, hostname } = window.location;
  if (isAppHostname(hostname)) return `${protocol}//${host}${p}`;
  return `${protocol}//${APP_HOST_LABEL}.${host}${p}`;
}

/**
 * Build-time flag for the tool portal. Same shape as ENABLE_TEMPLATE_RENDER_ROUTE
 * in App.tsx — on in dev, opt-in per production build. Vite inlines
 * `import.meta.env` at BUILD time, so there is no runtime toggle.
 */
export const TOOL_PORTAL_ENABLED =
  import.meta.env.MODE === "development" ||
  import.meta.env.VITE_TOOL_PORTAL_ENABLED === "true";

/**
 * Should this page render as the TOOL PORTAL — hub at `/`, rail as navigation,
 * app chrome instead of the marketing nav?
 *
 * Flag AND host, in one place, because two callers need the identical answer and
 * a disagreement between them is silent and ugly: App.tsx decides whether `/` is
 * the hub or the marketing home page, and MarketingLayout decides whether the
 * page gets the rail or MarketingNav. If those two ever diverged you would get
 * the marketing home page wearing the rail, or the hub wearing the marketing
 * menu.
 *
 * Note this is host-AND-flag, whereas `isAppHost()` on its own is host-only.
 * That distinction is deliberate: PageMeta's noindex branch keys off the HOST
 * alone, because app.wefixtrades.com serves duplicate content in both flag
 * states and must never be indexed in either.
 */
export function isToolPortalHost(): boolean {
  return TOOL_PORTAL_ENABLED && isAppHost();
}
